import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';

const LearningStatus = ({ statistics, loading, error }) => {
  if (loading) return (
    <div className="flex justify-center items-center p-4">
      <span className="loading loading-spinner loading-lg text-primary"></span>
    </div>
  );
  
  if (error) return (
    <div className="alert alert-error">
      <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>Entschuldigung, deine Statistiken konnten nicht geladen werden.</span>
    </div>
  );
  
  if (!statistics) return null;

  const calculatePercentage = (value) => ((value / statistics.total) * 100).toFixed(1);
  const greenPercentage = calculatePercentage(statistics.green);
  const yellowPercentage = calculatePercentage(statistics.yellow);
  const redPercentage = calculatePercentage(statistics.red);
  const whitePercentage = calculatePercentage(statistics.white);

  return (
    <div className="w-full">
      {/* Progress Bar with Legend */}
      <div className="space-y-3">
        {/* Title and Legend */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h3 className="font-medium text-sm">Lernfortschritt</h3>
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-x-4 gap-y-1 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-success ring-2 ring-success/20" />
              <span>{greenPercentage}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-warning ring-2 ring-warning/20" />
              <span>{yellowPercentage}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-error ring-2 ring-error/20" />
              <span>{redPercentage}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-base-content/20 ring-2 ring-base-content/10" />
              <span>{whitePercentage}%</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-2.5 w-full rounded-full overflow-hidden bg-base-300/50 ring-1 ring-base-content/10">
          <div className="h-full flex">
            <div 
              className="bg-success shadow-sm transition-all duration-300"
              style={{ width: `${greenPercentage}%` }} 
            />
            <div 
              className="bg-warning shadow-sm transition-all duration-300"
              style={{ width: `${yellowPercentage}%` }} 
            />
            <div 
              className="bg-error shadow-sm transition-all duration-300"
              style={{ width: `${redPercentage}%` }} 
            />
            <div 
              className="bg-base-content/20 shadow-sm transition-all duration-300"
              style={{ width: `${whitePercentage}%` }} 
            />
          </div>
        </div>
      </div>

      {/* Detailed Stats Disclosure */}
      <Disclosure>
        {({ open }) => (
          <>
            <DisclosureButton className="w-full flex items-center gap-2 mt-3 text-xs text-base-content/70 hover:text-base-content transition-colors">
              <ChevronDownIcon
                className={`${
                  open ? 'rotate-180' : ''
                } w-4 h-4 transition-transform duration-200`}
              />
              <span>Details anzeigen</span>
            </DisclosureButton>

            <DisclosurePanel className="mt-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="flex items-center gap-2 bg-success/10 rounded-lg p-2 ring-1 ring-success/20">
                  <CheckCircleIcon className="w-6 h-6 text-success" />
                  <div>
                    <div className="font-medium text-success">{statistics.green}</div>
                    <div className="text-xs text-base-content/70">Gemeistert</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 bg-warning/10 rounded-lg p-2 ring-1 ring-warning/20">
                  <ExclamationTriangleIcon className="w-6 h-6 text-warning" />
                  <div>
                    <div className="font-medium text-warning">{statistics.yellow}</div>
                    <div className="text-xs text-base-content/70">In Übung</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 bg-error/10 rounded-lg p-2 ring-1 ring-error/20">
                  <XCircleIcon className="w-6 h-6 text-error" />
                  <div>
                    <div className="font-medium text-error">{statistics.red}</div>
                    <div className="text-xs text-base-content/70">Falsch</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 bg-base-200 rounded-lg p-2 ring-1 ring-base-content/10">
                  <QuestionMarkCircleIcon className="w-6 h-6 text-base-content/60" />
                  <div>
                    <div className="font-medium text-base-content">{statistics.white}</div>
                    <div className="text-xs text-base-content/70">Neu</div>
                  </div>
                </div>
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  );
};

export default LearningStatus;